import { useRef } from 'react';

/**
 * `yyyy-MM-dd` 문자열을 「YYYY년 M월 D일」로 포맷한다 — 이 패키지가 소유하는 날짜 표시 문구의 정본.
 *
 * ## 왜 `@safetyhub/shared`의 것을 쓰지 않나
 * shared에는 `formatDateOnly`가 있지만 **표시 문구가 다르다**(「2026. 10. 27.」). 사용자가 지목한
 * 목표 화면(점검 예약의 「점검일」)은 「2026년 10월 27일」이고, shared 쪽으로 맞추면 공용화가 곧
 * **기존 9자리의 시각 변경**이 된다. shared에 「YYYY년 M월 D일」 함수를 **추가하는** 길도 있지만
 * 이 라운드의 담당 파일이 아니라 손대지 않았다 — 옮길 값이 있으면 별 라운드로.
 *
 * ## 왜 호출부 prop(`formatValue`)만으로 두지 않나
 * 사용자 지시가 「모든 날짜 선택 UI를 **동일하게**」다. 포맷을 전부 prop으로 받으면 통일을
 * 기계가 지키지 못하고, 실제로 **admin 8자리에는 넘길 포맷 함수가 아예 없었다**
 * (`apps/admin`에 날짜 표시 헬퍼 0건 — 전수 grep). 그래서 **기본값이 통일을 보장**하고
 * 예외 자리만 `formatValue`로 덮는다.
 *
 * ## 빈 값·깨진 값
 * 빈 문자열은 **빈 문자열**을 돌려준다(호출부가 placeholder로 분기하도록). `yyyy-MM-dd` 형태가
 * 아니면 **원문을 그대로** 돌려준다 — **없는 날짜를 꾸며내지 않는 쪽이 정본이다.** 걷어낸 web
 * 사본 둘이 그 반대였다: 하나는 `split` 결과를 그대로 `parseInt`해 `undefined년 NaN월 NaN일`을
 * 만들었고, 다른 하나는 `Date`를 거쳐 `2026-02-29`를 **「2026년 3월 1일」로 옮겨 적고** 날짜가
 * 아닌 값에는 **「Invalid Date」**를 화면에 냈다. 되짚을 앵커는 둘이고 **한 일이 다르다**:
 * 앞엣것은 파일째 지운 커밋 `0aacef364`(web 유틸 한 벌 — 그 파일의 유일한 export였다),
 * 뒷것은 화면 모듈 안의 사본을 걷은 커밋 `970d9845c`(파일 삭제가 아니라 함수 제거다).
 * ⛔ 이 갈래를 「아무도 안 쓰는 방어」로 읽고 걷지 말 것: 그 두 사본은 **유효한 날짜에서는 이
 * 함수와 출력이 글자까지 같았고**, 갈린 것이 정확히 이 갈래다(그래서 되돌려도 red가 없다).
 *
 * `Date` 객체를 거치지 않는 **순수 문자열 포맷**이라 시간대 오프셋 오류가 구조적으로 없고,
 * `toLocaleDateString`을 부르지 않아 루트 eslint의 `no-restricted-syntax`와도 무관하다.
 */
export function formatDateDisplay(value: string): string {
  if (!value) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return value;
  return `${m[1]}년 ${Number(m[2])}월 ${Number(m[3])}일`;
}

/**
 * `yyyy-MM-dd`를 「2026.10.27」로 포맷한다 — **필터 줄의 좁은 자리**가 쓰는 짧은 표기.
 *
 * ## 왜 두 번째 포맷터인가 (문구가 셋인 것은 사고가 아니다)
 * 같은 패키지의 `formatDateDisplay`(「2026년 10월 27일」)·`@safetyhub/shared`의
 * `formatDateOnly`(「2026. 10. 27.」)와 **문구가 셋 다 다르다**. 필터 알약은 한 줄에 현장·상태·
 * 기간이 나란히 놓이는 자리라 「년 월 일」을 쓰면 줄이 넘치고, shared 쪽은 점 뒤 공백이 있어
 * 역시 길다. 그래서 **문구를 통일하는 대신 포맷터를 하나 더 두고** 기존 표기를 보존했다 —
 * 이관을 「무변경 치환」으로 유지하는 것이 이 라운드의 전제다(표기를 바꾸면 그것은 이관이
 * 아니라 **화면 변경**이고 사용자 결정이 필요하다).
 *
 * ## 무엇의 사본을 걷었나
 * 2026-10-27까지 이 문구가 web 두 자리에 **손사본**으로 있었다:
 * `inspection/layouts/InspectionListDesktop.tsx`의 `DateFilterPill`(`split('-').join('.')`)과
 * `pages/Dashboard/components/DateRangeFilter.tsx`(ISO를 `new Date`로 받아 로컬 getter로
 * 재조립). 뒤엣것은 **입력이 ISO 문자열**이라 모양이 달라 보이지만, 호출부가 넘기던 값이
 * 이미 `toLocalDateString`을 거친 로컬 날짜라 **유효 입력에서 출력이 글자까지 같다**.
 *
 * ⛔ 갈리는 입력은 **부분 날짜**(`2026-10`)뿐이다 — `split` 사본은 `2026.10`을 만들고 이쪽은
 * **원문**을 돌려준다(`formatDateDisplay`와 같은 규약: 없는 날짜를 꾸며내지 않는다). 그 입력이
 * 그 자리에 **도달하지 않는다**는 것을 확인하고 바꿨다: `filters.startDate`·`endDate`의 쓰기
 * 경로가 그 알약의 date input **하나**이고(`useInspectionListPage`의 초기값은 `status`만 URL에서
 * 읽는다 — 날짜는 **URL 축이 아니다**) 네이티브 date input은 `yyyy-MM-dd` 또는 빈 문자열만 준다.
 * ⇒ 도달 가능한 전 입력에서 동일하다. 이 전제가 깨지는 변경(날짜를 URL 쿼리로 받기 등)을 하면
 * 그때 이 갈래를 다시 볼 것.
 */
export function formatDateCompact(value: string): string {
  if (!value) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return value;
  return `${m[1]}.${m[2]}.${m[3]}`;
}

/** 버튼 밀도. 값은 완성형 리터럴 두 벌이다(아래 `TRIGGER_CLASS` 주석 참조). */
export type DateInputSize = 'sm' | 'md';

/**
 * 트리거 버튼의 **모양** 클래스 — 밀도별 «완성형 리터럴». **색은 여기 없다** — 테두리·면·글자
 * 색은 전부 아래 `STATE_CLASS`가 소유한다(여기 `border`는 테두리 «두께»뿐이다). 왜 갈랐는지는
 * 그 상수의 머리말이 정본이다.
 *
 * Tailwind는 소스를 텍스트로 스캔하므로 `px-${n}` 같은 동적 조합을 쓰면 클래스만 붙고 CSS가 없는
 * 무스타일이 된다(`controlClasses.ts` 머리말과 같은 규약). 그래서 두 값을 각각 통째로 적는다.
 *
 * - `md`: 사용자가 지목한 목표 화면(`apps/web/.../CreateInspectionModal.tsx`의 「점검일」)의 값.
 * - `sm`: 필터 줄처럼 촘촘한 자리(`apps/web/.../tbm-distribution-setup.tsx`의 `size="sm"`)의 값.
 *   ⛔ 종전에 그 자리를 `triggerClass`라 적었고 **그 심볼은 그 파일에 없었다** — 레포 전체에서
 *   그 주석 한 줄에만 있었고 비슷한 이름은 `DropdownMenu`의 것이다. `comment-symbol-exists`
 *   가드의 축 (e)가 그것을 잡아 **master를 red로** 만들었다(배포 게이트). ⇒ 남의 파일을 가리킬
 *   때는 **그 파일에 실재하는 표기**를 쓴다 — 추측한 변수명은 red 없이 낡고 그 가드가 유일한
 *   탐지기다.
 *
 * **폭은 일부러 뺐다** — 소비처가 `w-full`·`w-44`로 각각 정한다(실측: 이관 대상 8자리 중 `w-full`이
 * 2곳, 나머지는 내용폭). `controlClasses.ts`의 `FILTER_SELECT_CLASS`와 같은 분담이다.
 *
 * ## ⛔ `className`으로 이 컴포넌트의 색을 **덮을 수 없다** — 그래서 색이 여기 없다
 * `className`은 클래스 문자열 **뒤에** 이어 붙지만, Tailwind에서 같은 속성의 승패를 정하는 것은
 * class 속성의 순서가 아니라 **생성된 CSS의 순서**다. 그래서 색을 호출부에서 넘기는 설계가
 * 원리적으로 성립하지 않고, 상태색을 `STATE_CLASS`가 **소유**한다. 측정값과 재는 법은
 * 그 상수의 머리말이 정본이다.
 *
 * 반대로 **이 리터럴에 «없는» 속성은 `className`으로 그냥 걸린다** — 충돌할 상대가 없기
 * 때문이다. 폭(`w-full`)이 그 설계고, 같은 이유로 높이·굵기도 넘길 수 있다
 * (`AssignRoleModal`이 `h-[42px] font-semibold`로 `FORM_CONTROL_CLASS` 줄에 맞춘다 —
 * 이 리터럴에 `h-`·`w-`·`font-` 가 하나도 없는 것이 그 전제다).
 * ⛔ 「없으면 걸린다」를 **크기·간격까지 넓히지 말 것** — `gap-`·`text-sm`·`px-`는 여기 **있다**:
 * 2026-10-27 실측으로 `gap-1.5`(5) < `gap-2`(9) · `text-[14.5px]`(79) < `text-sm`(83)이라
 * **줄이는 방향은 조용히 무시되고**, `px-2`(51) < `px-3`(56)이라 **키우는 방향만** 걸린다.
 * 즉 이 축은 「속성이 있나」만이 아니라 **「어느 값이 뒤에 오나」**까지 봐야 한다.
 */
const SHAPE_CLASS: Record<DateInputSize, string> = {
  md: 'inline-flex items-center gap-2 rounded-control border px-3 py-2 text-left text-sm shadow-card-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:cursor-not-allowed disabled:bg-ink-100 disabled:hover:border-line',
  sm: 'inline-flex items-center gap-2 rounded-control border px-2 py-1.5 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:cursor-not-allowed disabled:bg-ink-100 disabled:hover:border-line',
};

/**
 * 상태별 **색** — 테두리·면·글자를 한 벌로 묶은 완성형 리터럴 셋. 이 컴포넌트가 **소유**한다.
 *
 * ## ⛔ 왜 호출부가 못 넘기나 (2026-10-27 실측 · 2차 재측에서 확증)
 * preset의 색 선언 순서(`brand` → `ink` → `surface` → `line`)가 그대로 emit 순서가 되므로,
 * 호출부가 활성색을 `className`으로 넘기면 **이쪽 중립색이 뒤에 와서 이긴다** — 색은 붙는데
 * 「어느 기간이 선택됐나」 표시가 **사라지고 red가 없다**(tsc·spec·eslint 전부 통과):
 *
 * | 호출부가 넘긴 것 | 중립색 | 이기는 쪽 |
 * |---|---|---|
 * | `border-brand-500`(26) | `border-line`(31) | **`border-line`** |
 * | `bg-brand-50`(36) | `bg-white`(46) | **`bg-white`** |
 * | `text-brand-700`(101) | `text-ink-900`(121) | **`text-ink-900`** |
 *
 * (괄호는 생성된 CSS의 선택자 줄 번호다. 재는 법: preset만 얹은 config로 그 클래스들을 담은
 * 파일을 `tailwindcss`에 통과시켜 출력 CSS의 줄 순서를 본다. ⚠️ preset이 `export default`라
 * config를 **`.mjs`로 쓸 것** — `require`로 가져오면 `{ default: … }`가 들어가 **토큰이 조용히
 * 하나도 생성되지 않고**, 그러면 「충돌이 없다」는 거짓 결론이 나온다. 실제로 1차 측정이 그렇게
 * 빗나갔다. ⇒ **[대조군] 출력에 `.border-line`이 실재하는지 먼저 세라** — 그것이 계측기 생존
 * 신호이고, 없으면 그 측정은 「충돌 없음」이 아니라 **「안 쟀다」**다.)
 *
 * ## 왜 세 키인가 — placeholder 톤이 활성색을 덮는 조합을 «구조적으로» 없앤다
 * 종전 구현은 중립색 리터럴 뒤에 `display ? '' : ' text-ink-400'`을 **덧붙였다**. 활성색을
 * 더하면서 그 방식을 유지하면 `text-ink-400`(106)이 `text-brand-700`(101)을 **이겨** 활성인데
 * 값이 없는 칸의 활성색이 조용히 사라진다 — 이 라운드가 막으려는 함정이 컴포넌트 **안에서**
 * 재현되는 자리다. 그래서 상태를 **상호배타 키 하나**(`active ? 'active' : display ? 'filled'
 * : 'empty'`)로 정하고 글자색을 각 리터럴이 **통째로** 들게 했다. 조합이 생길 수 없으므로
 * 규율이 아니라 **형상**이 그것을 막는다.
 *
 * ## 선례 — `Chip`
 * 같은 패키지의 `Chip`이 이미 같은 설계다(base는 `border` 두께만, `STATE_CLASS`가 선택색을
 * 소유). 그 머리말의 「**hover는 선택되지 않았을 때만** 바뀐다 — 선택된 칩에 hover 변화를 주면
 * 『선택됨』과 『가리킴』이 같은 방향으로 밝아져 구분이 흐려진다」를 여기도 따라서
 * **`active`에는 `hover:` 가 없다**(이관 전 `DateRangeFilter`의 활성 분기도 hover가 없었다 —
 * 규약과 그 자리의 실코드가 일치한다).
 *
 * ⚠️ 목표 화면(web `CreateInspectionModal`)은 테두리를 `border-ink-300`으로 들고 있었고 여기는
 * **`border-line`**(디자인 토큰)이다. 토큰 쪽이 정본이라 그것으로 모았으므로 2026-10-27 web
 * 이관에서 실제로 여덟 자리가 `#cbd5e1` → `#e8ecf2`로 옅어졌다(이관을 「무변경 치환」으로 적지
 * 말 것).
 *
 * ## 이 상수가 «재지 않는» 축 — 활성/비활성을 색만이 가르는 화면이 있다
 * `DateRangeFilter`에서 preset(오늘·1주일·한달)을 고르면 두 칸에 **날짜 문구가 똑같이** 뜨고
 * 활성만 아니다 ⇒ 그 화면에서 「직접 고른 기간인가」를 가르는 것은 **색 하나**다. 이관이
 * 그 상태를 그대로 옮긴 것이고 개선하지 않았다 — `aria-pressed`를 달면 **토글이 아닌 것을
 * 토글이라 말하는** 거짓이 되고(`Chip`의 「찾을 수 없는 것을 약속하지 말 것」과 같은 축),
 * 비색 단서를 더하는 것은 **디자인 결정**이라 이 컴포넌트가 혼자 정할 자리가 아니다.
 * ⇒ 소유자는 그 화면이고, 이 리터럴의 초록을 「색 외 단서가 있다」의 근거로 인용하지 말 것.
 */
const STATE_CLASS = {
  active: 'border-brand-500 bg-brand-50 text-brand-700',
  filled: 'border-line bg-white text-ink-900 hover:border-brand-400',
  empty: 'border-line bg-white text-ink-400 hover:border-brand-400',
} as const;

export interface DateInputProps {
  /** 값 — `yyyy-MM-dd` 문자열. 빈 문자열이면 미선택(버튼에 `placeholder`가 보인다). */
  value: string;
  /** 다음 값. 사용자가 값을 지우면 **빈 문자열**이 온다(네이티브 date input의 동작). */
  onChange: (next: string) => void;
  /**
   * 이 칸이 무엇인가 — **접근명의 유일한 출처**. 숨은 입력은 이 이름을, 버튼은 `<label> 선택`을 받는다.
   * 둘에 같은 이름을 붙이면 `getByLabelText`가 모호해져 기존 단언이 통째로 깨지므로 갈라 둔 것이고,
   * 그 규약을 손이 아니라 이 컴포넌트가 지킨다(아래 머리말의 「접근명」 절).
   */
  label: string;
  /** 미선택 때 버튼에 보일 문구. 기본 `날짜를 선택하세요`. */
  placeholder?: string;
  /** 숨은 입력의 `id` — e2e 셀렉터·`<label htmlFor>` 계약이 걸리는 자리라 입력에 그대로 단다. */
  id?: string;
  /** 폼 제출용 필드명. 숨은 입력이 값의 정본이라 여기 달린다. */
  name?: string;
  /** 선택 하한(`yyyy-MM-dd`). */
  min?: string;
  /** 선택 상한(`yyyy-MM-dd`). */
  max?: string;
  /** 비활성 — 버튼과 숨은 입력 **양쪽**에 걸린다(한쪽만 걸면 입력으로 값이 들어온다). */
  disabled?: boolean;
  /** 폼 필수. 숨은 입력에 달린다. */
  required?: boolean;
  /** 버튼 밀도. 기본 `md`. */
  size?: DateInputSize;
  /**
   * **이 칸이 지금 활성(선택된 필터)인가** — 참이면 brand 색 한 벌(`STATE_CLASS.active`)로 그린다.
   *
   * ## 왜 `selected`가 아닌가
   * `value`가 이미 「날짜가 선택됐다」를 말한다. 그 뜻으로 `selected`를 쓰면 두 prop이 같은 말을
   * 하는 것처럼 읽히고, 실제 축은 다르다 — `DateRangeFilter`는 preset으로 기간을 고를 때도
   * `value`가 **차 있지만** 그 칸은 활성이 아니다(활성 = 사용자가 **이 칸으로 직접** 골랐다).
   *
   * ## ARIA를 붙이지 않는다
   * 이 버튼은 눌러서 달력을 여는 것이고 **토글이 아니다**. `aria-pressed`를 달면 보조기술에
   * 「지금 꺼져 있는 토글」이라 거짓말을 한다(`Chip`의 `selected` 머리말과 같은 판단).
   * ⚠️ 그래서 **활성/비활성이 색만으로 갈리는 화면이 남는다** — 그 한계의 소유자와 이유는
   * `STATE_CLASS` 머리말의 「재지 않는 축」 절이다.
   */
  active?: boolean;
  /** 버튼에 덧붙일 클래스 — **폭은 여기서** 준다(`w-full` 등). */
  className?: string;
  /** 표시 문구 override. 기본은 `formatDateDisplay`. */
  formatValue?: (value: string) => string;
  /** 테스트·실측용 식별자. 버튼에 달린다. */
  'data-testid'?: string;
}

/**
 * 공용 날짜 선택 입력 — **보이는 것은 버튼, 값의 정본은 `sr-only` date input**이고 버튼이
 * 네이티브 달력 팝업(`showPicker()`)을 연다.
 *
 * ## 왜 bare `<input type="date">`가 아닌가
 * bare 입력은 브라우저가 그린 **달력 글리프를 정확히 눌러야만** 열려, 칸 본문을 눌러도 아무 일이
 * 없다 — 검수에서 「클릭 지점에 따라 달력이 안 열린다」로 적발된 자리다. 그래서 코드베이스가
 * 버튼+숨은 입력 규약을 쓰는데, 그것이 **공용 컴포넌트가 아니라 6파일에 복제된 인라인 패턴**이었고
 * (`showPicker` 호출부 10자리) 그래서 admin 8자리에는 아예 적용되지 않았다. 이 컴포넌트가 그
 * 규약의 단일 출처다.
 *
 * ⛔ 숨김은 반드시 `sr-only`(clip)여야 한다 — `display:none`/`hidden`이면 브라우저가
 * `showPicker()`를 **거부**한다. 그 자리의 종전 정본 주석은
 * `apps/web/src/components/inspection/layouts/InspectionListDesktop.tsx`의 `DateFilterPill`이다.
 *
 * ## 접근명 (이 공용화가 바로잡는 것)
 * `label` 하나에서 **버튼 = `<label> 선택` · 입력 = `<label>`**을 파생시킨다. 이관 전 실측으로
 * admin 8자리는 **접근명이 0건**이었다(세 자리는 `htmlFor` 없는 `<label>` 텍스트만 옆에 있었고
 * 그것은 프로그램적 이름이 아니다). web 쪽도 목표 화면의 입력에는 `aria-label`이 없다.
 * 손으로 두 자리에 적던 규약을 파생으로 바꾸면 한쪽만 빠지는 일이 구조적으로 없어진다.
 *
 * **`tabIndex={-1}`은 유지한다**(종전 규약 그대로). 떼면 보이지 않는 컨트롤이 탭 순서에 들어와
 * **포커스가 화면에서 사라지고** 한 칸에 탭 정지가 둘이 된다. 키보드 사용자는 버튼으로 달력을 열고,
 * 그 활성화는 사용자 제스처라 `showPicker()`가 허용된다.
 *
 * **`aria-haspopup`은 일부러 달지 않았다** — 네이티브 날짜 팝업은 접근성 트리에 `dialog`로 올라오지
 * 않으므로 `aria-haspopup="dialog"`는 보조기술에 **찾을 수 없는 것을 약속**한다.
 *
 * ## 못 재는 축
 * `showPicker()`가 거부(`NotAllowedError`)되면 폴백은 **네이티브 기본 동작**(입력 자신의 달력
 * 글리프)인데, 그 글리프는 `sr-only` 입력 위에 있어 **키보드로 닿지 않는다**. 즉 거부 환경에서는
 * 키보드만으로 날짜를 고를 수 없다 — 종전 6파일 전부가 가진 한계를 그대로 물려받은 것이고,
 * 고치려면 자체 달력이 필요해(현재 외부 날짜 라이브러리 0건) 이 라운드 밖이다.
 * 버튼 폭·정렬·팝업 위치는 jsdom에 레이아웃 엔진이 없어 spec이 재지 못한다(짝 spec 머리말 참조).
 */
export function DateInput({
  value,
  onChange,
  label,
  placeholder = '날짜를 선택하세요',
  id,
  name,
  min,
  max,
  disabled,
  required,
  size = 'md',
  active,
  className,
  formatValue = formatDateDisplay,
  'data-testid': dataTestId,
}: DateInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  // 빈 값에 `formatValue` 를 **부르지 않는다**. 기본 포맷터는 빈 값에 빈 문자열을 돌려주므로 이
  // 분기 없이도 placeholder 는 보인다 — 되돌림 프로브 실측 **0 red**로 확인했다. 이 분기가 지키는
  // 것은 **호출부가 넘긴 `formatValue` 의 계약**이다: 빈 값을 안 받으므로 커스텀 포맷터가
  // `undefined년 NaN월` 같은 것을 만들거나 던질 수 없다. 그래서 그 축의 탐지기는 「표시 문구」가
  // 아니라 **「빈 값에는 formatValue 를 부르지 않는다」** 케이스다(지우면 그 축이 0 이 된다).
  const display = value ? formatValue(value) : '';

  // 상태는 **키 하나**로 정한다 — 색 리터럴을 겹쳐 붙이지 않는 것이 요점이다(`STATE_CLASS`
  // 머리말의 「왜 세 키인가」). `active` 가 `display` 보다 앞에 오는 것은 활성인데 값이 없는
  // 조합에서 **placeholder 톤이 아니라 활성색**을 보여야 하기 때문이다.
  const state = active ? 'active' : display ? 'filled' : 'empty';

  // 래퍼를 두지 않는다(Fragment) — 폭을 `className`으로 받는 설계라 래퍼가 있으면 `w-full`이 버튼에만
  // 걸려 래퍼 폭에 갇힌다. `sr-only`는 `position:absolute`이므로 숨은 입력은 흐름에서 빠져,
  // 소비처가 `flex gap-2` 줄이어도 유령 간격을 만들지 않는다(숨긴 요소를 감싼 래퍼가 흐름에 남으면
  // grid 행·flex gap을 만든다는 그 함정의 반대편이다).
  return (
    <>
      <button
        type="button"
        aria-label={`${label} 선택`}
        disabled={disabled}
        data-testid={dataTestId}
        onClick={() => openDatePicker(inputRef.current)}
        className={`${SHAPE_CLASS[size]} ${STATE_CLASS[state]}${className ? ` ${className}` : ''}`}
      >
        <CalendarGlyph />
        <span className="truncate">{display || placeholder}</span>
      </button>
      <input
        ref={inputRef}
        type="date"
        id={id}
        name={name}
        aria-label={label}
        value={value}
        min={min}
        max={max}
        disabled={disabled}
        required={required}
        tabIndex={-1}
        onChange={(e) => onChange(e.target.value)}
        className="sr-only"
      />
    </>
  );
}

/**
 * 숨은 date input의 달력을 연다 — **이 규약의 단일 출처**.
 *
 * `showPicker`는 lib.dom 버전에 따라 타입이 없을 수 있고 구형 브라우저엔 아예 없다 — 옵셔널 호출로
 * 둔다. 있어도 사용자 제스처 밖 호출 등에서 `NotAllowedError`를 **던지므로** try/catch로 감싸고
 * 네이티브 기본 동작에 맡긴다.
 * (같은 규약의 형제 구현: `packages/report-composer/src/DateFieldInput.tsx` — 그쪽은 투명 오버레이
 * 입력이라 이 패키지의 Tailwind 클래스 규약을 쓸 수 없어 별 구현으로 남는다.)
 *
 * ## 왜 export 하나 — 그리고 떼지 말아야 하는 이유
 * `DateInput` 자신 말고 **공용 모양으로 덮이지 않는 자리**가 이것을 쓴다(2026-10-27 현재
 * `InspectionListDesktop`의 `DateFilterPill` 하나 — 그 자리는 라벨+값 배지라 트리거 내용 슬롯이
 * 없고, 형제 `FilterPill`과 치수 계약이 걸려 있어 공용 셸로 옮기지 않았다). 그 자리가 **try/catch
 * 없이** `showPicker()`를 부르고 있었다.
 *
 * ⛔ 감싸지 않았을 때 깨지는 것을 **「화면이 죽는다」로 적지 말 것** — 짝 spec의 프로브가 그 예측을
 * 반증했다: React가 핸들러의 throw를 window `error`로 **다시 던지므로** 언마운트가 일어나지 않고
 * 마운트 단언은 그대로 통과한다. 그래서 이 축의 탐지기는 「버튼이 아직 있다」가 아니라
 * **window error를 세는 단언**이다(`DateInput.test.tsx`의 그 케이스).
 */
export function openDatePicker(el: HTMLInputElement | null) {
  const input = el as (HTMLInputElement & { showPicker?: () => void }) | null;
  try {
    input?.showPicker?.();
  } catch {
    // 무시 — 폴백은 네이티브 기본 동작이다.
  }
}

/**
 * 달력 아이콘 — `apps/web/src/components/icons/index.tsx`의 `CalendarIcon`과 **같은 path**다.
 * 이 패키지는 아이콘 의존이 0건이고(레포 전체에 heroicons 0건) 형제 컴포넌트(`Modal`·
 * `SelectableCard`)도 `<svg>`를 인라인으로 들고 있어 같은 방식을 따랐다. 사본인 이유는 그 아이콘이
 * **앱 쪽 모듈**이라 공용 패키지가 역방향으로 의존할 수 없기 때문이고, 다음 라운드에 web이 이
 * 컴포넌트를 쓰면 그 자리의 import가 줄어든다.
 */
function CalendarGlyph() {
  return (
    <svg
      className="h-4 w-4 shrink-0 text-ink-400"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
      />
    </svg>
  );
}
